import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject, ViewChild } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { DialogBaseComponent } from '@/app/core/components';
import { SopFormComponent, SopFormData } from '../sop-form/sop-form.component';
import { SopResponseDto } from '@qc/shared';
import { SopService } from '@/app/core/services/sop.service';
import { formatDateOnly } from '@/app/shared/utils';

@Component({
    selector: 'qc-sop-dialog',
    standalone: true,
    imports: [CommonModule, SopFormComponent, ButtonModule],
    template: `
        <div class="w-[800px] max-w-[90vw] bg-white rounded-lg shadow-2xl" style="overflow: visible !important;">
            <div class="flex justify-between items-center p-6 border-b border-gray-200">
                <h2 class="text-xl font-semibold text-gray-900">
                    {{ isEditMode ? 'Edit SOP' : (deferredSave ? 'Select SOP' : 'Create New SOP') }}
                </h2>
                <button type="button" class="p-2 hover:bg-gray-100 rounded-lg" (click)="close()">
                    <i class="pi pi-times text-gray-400"></i>
                </button>
            </div>

            <div class="p-6 max-h-[70vh]" style="overflow: visible !important;">
                <qc-sop-form
                    [sop]="initialData"
                    [readonly]="false"
                    [showButtons]="false"
                    (loading)="onLoadingChange($event)"
                    (validityChange)="onValidityChange($event)"
                />
            </div>

            <div class="flex justify-end gap-3 p-6 border-t border-gray-200">
                <p-button label="Cancel" severity="secondary" variant="outlined" (click)="close()" class="mr-2" />
                <p-button
                    [label]="isEditMode ? 'Update' : 'Create'"
                    severity="primary"
                    (click)="handleSave()"
                    [disabled]="!isFormValid || isLoading"
                    [loading]="isLoading"
                />
            </div>
        </div>
    `,
    styles: [
        `
            :host ::ng-deep .p-calendar {
                z-index: 9999;
            }
            :host ::ng-deep .p-dropdown-panel {
                z-index: 9999;
            }
            :host ::ng-deep .p-calendar-panel {
                position: absolute !important;
                z-index: 10000 !important;
            }
        `,
    ],
})
export class SopDialogComponent extends DialogBaseComponent implements OnInit {
    @Input() initialData: SopResponseDto | null = null;
    @Input() deferredSave = false;
    @Output() sopSaved = new EventEmitter<SopResponseDto>();
    @ViewChild(SopFormComponent) sopFormComponent!: SopFormComponent;

    private sopService = inject(SopService);

    isLoading = false;
    isFormValid = false;

    get isEditMode(): boolean {
        return !!this.initialData?.id;
    }

    constructor() {
        super();
    }

    ngOnInit() {
        // Component initialization
    }

    onLoadingChange(loading: boolean): void {
        this.isLoading = loading;
    }

    onValidityChange(isValid: boolean): void {
        this.isFormValid = isValid;
    }

    handleSave(): void {
        if (this.sopFormComponent && this.sopFormComponent.form.valid) {
            const formData: SopFormData = this.sopFormComponent.form.value;
            this.onSopSaved(formData);
        }
    }

    onSopSaved(sopData: SopFormData): void {
        // Filter out empty strings - convert them to undefined
        const cleanData = { ...sopData };
        Object.keys(cleanData).forEach((key) => {
            const value = (cleanData as any)[key];
            if (typeof value === 'string' && value.trim() === '') {
                (cleanData as any)[key] = undefined;
            }
        });

        const requestData = {
            ...cleanData,
            revisionDate: formatDateOnly(sopData.revisionDate),
            nextReviewDate: formatDateOnly(sopData.nextReviewDate),
            lastSyncedAt: sopData.lastSyncedAt ? sopData.lastSyncedAt.toISOString() : undefined,
        };

      
        if (this.deferredSave) {
          
            const sopToEmit = this.isEditMode && this.initialData?.id
                ? { ...requestData, id: this.initialData.id } as SopResponseDto
                : { ...requestData, id: `temp-${Date.now()}-${Math.random()}`, _isNew: true } as any;

            this.sopSaved.emit(sopToEmit);
            this.close(true);
            return;
        }

        this.isLoading = true;

        if (this.isEditMode && this.initialData?.id) {
            this.sopService.updateSop(this.initialData.id, requestData).subscribe({
                next: (updatedSop) => {
                    this.isLoading = false;
                    const sopData = (updatedSop as any)?.data || updatedSop;
                    this.sopSaved.emit(sopData);
                    this.close(true);
                },
                error: (error) => {
                    this.isLoading = false;
                    console.error('Error updating SOP:', error);
                    this.close(false);
                },
            });
        } else {
            this.sopService.createSop(requestData).subscribe({
                next: (createdSop) => {
                    this.isLoading = false;
                    const sopData = (createdSop as any)?.data || createdSop;
                    this.sopSaved.emit(sopData);
                    this.close(true);
                },
                error: (error) => {
                    this.isLoading = false;
                    console.error('Error creating SOP:', error);
                    this.close(false);
                },
            });
        }
    }
}
